import { lighten } from '@material-ui/core/styles';
import { ThemeOverrides } from '@roc/feature-types';
import { createElement } from 'react';
import {
  RoundedCheckboxCheckedIcon,
  RoundedCheckboxIcon,
  RoundedCheckboxIndeterminateIcon,
} from './checkboxIcons';
import { ChevronDownIcon } from './selectIcons';

/**
 * Platform design system tokens (mirrors platform-frontend design-system.css / shadcn globals.css).
 * Keep MUI styling driven from here so the look can be changed in one place.
 */
export const platformTokens = {
  radius: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 24,
    pill: 999,
  },
  color: {
    canvas: '#F6F7FB', // semantic-25
    surface: '#FFFFFF',
    foreground: '#252525',
    heading: '#1B1D34', // neutral-900
    mutedForeground: '#34395E', // neutral-800
    subtle: '#5E69A6', // neutral-700
    border: '#EBEBEB',
    neutral50: '#F7F8FC',
    neutral100: '#E7EAF8', // accent
    neutral200: '#DADEF4',
    input: 'rgba(46, 98, 255, 0.3)',
    inputHover: 'rgba(46, 98, 255, 0.6)',
    ring: '#2E62FF', // info-600
    ringShadow: 'rgba(46, 98, 255, 0.2)',
  },
  shadow: {
    sm: '0 1px 3px 0 rgba(69, 69, 127, 0.08), 0 1px 2px -1px rgba(69, 69, 127, 0.08)',
    md: '0 4px 12px -2px rgba(69, 69, 127, 0.12), 0 2px 4px -2px rgba(69, 69, 127, 0.08)',
    // all-round soft shadow with a faint outline so cards stay distinct on a white page
    lg: '0 0 0 1px rgba(69, 69, 127, 0.05), 0 2px 4px rgba(69, 69, 127, 0.04), 0 12px 32px -4px rgba(69, 69, 127, 0.10)',
    xl: '0 24px 48px -12px rgba(69, 69, 127, 0.24)',
  },
  status: {
    success: { bg: '#D8FFF6', border: '#6BE3CE', text: '#006155' },
    info: { bg: '#F0F5FF', border: '#ADC9FF', text: '#0034D1' },
    warning: { bg: '#FFF3E5', border: '#FFC072', text: '#784B00' },
    error: { bg: '#FFF1F2', border: '#FFB5B8', text: '#9E0030' },
  },
};

/**
 * Platform font. White-label portals can supply their own font in portalConfiguration
 * (uiThemeConfiguration.typography). While this is set, it wins over the portal font.
 * Set it to `undefined` to let the portal configuration fonts take over again.
 */
export const PLATFORM_FONT_FAMILY: string | undefined =
  '"Figtree", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export const commonPalette = {
  common: {
    black: '#000000DE',
    white: '#fff',
  },
  background: {
    paper: platformTokens.color.surface,
    // White page so tabs, chips and tinted headers stand out (canvas tint is kept for accents)
    default: platformTokens.color.surface,
  },
  success: {
    light: '#53CEB9',
    main: '#02A591',
    dark: '#008777',
  },
  error: {
    light: '#FF6B7B',
    main: '#FF2E5B',
    dark: '#D90045',
  },
  warning: {
    light: '#F6A52C',
    main: '#CB8300',
    dark: '#A66B00',
  },
  info: {
    light: '#6A9AFF',
    main: '#4880FF',
    dark: '#2E62FF',
  },
  text: {
    primary: platformTokens.color.foreground,
    secondary: platformTokens.color.mutedForeground,
  },
  divider: platformTokens.color.border,
  action: {
    hover: platformTokens.color.neutral50,
    selected: platformTokens.color.neutral100,
  },
  primary: {
    main: '#21395e',
  },
  secondary: {
    main: '#00B2FF',
  },
};

const { radius, color, shadow, status } = platformTokens;

/** MUI needs 25 shadow levels; map them onto the platform's 4-step scale. */
export const platformShadows = [
  'none',
  ...Array(2).fill(shadow.sm),
  ...Array(6).fill(shadow.md),
  ...Array(8).fill(shadow.lg),
  ...Array(8).fill(shadow.xl),
] as any;

export const getPlatformTypography = (typography: any = {}) => {
  const fontFamily = PLATFORM_FONT_FAMILY ?? typography.fontFamily;
  const heading = (variant: string) => ({
    ...typography[variant],
    ...(fontFamily ? { fontFamily } : {}),
    fontWeight: 600,
    letterSpacing: 'normal',
  });
  return {
    ...typography,
    ...(fontFamily ? { fontFamily } : {}),
    h1: heading('h1'),
    h2: heading('h2'),
    h3: heading('h3'),
    h4: heading('h4'),
    h5: heading('h5'),
    h6: heading('h6'),
    button: {
      ...typography.button,
      ...(fontFamily ? { fontFamily } : {}),
      fontWeight: 600,
      letterSpacing: 'normal',
      textTransform: 'none',
    },
  };
};

export const commonMuiThemeProps = {
  MuiButton: {
    disableElevation: true,
  },
  // Thin chevron for every dropdown (platform style)
  MuiSelect: {
    IconComponent: ChevronDownIcon,
  },
  MuiNativeSelect: {
    IconComponent: ChevronDownIcon,
  },
  MuiAutocomplete: {
    popupIcon: createElement(ChevronDownIcon),
  },
  // Rounded-square checkboxes everywhere
  MuiCheckbox: {
    icon: createElement(RoundedCheckboxIcon),
    checkedIcon: createElement(RoundedCheckboxCheckedIcon),
    indeterminateIcon: createElement(RoundedCheckboxIndeterminateIcon),
  },
  MuiButtonBase: {
    disableRipple: true,
  },
};

const statusAlert = (key: keyof typeof status) => ({
  backgroundColor: status[key].bg,
  color: status[key].text,
  border: `1px solid ${status[key].border}`,
  '& $icon': {
    color: status[key].text,
  },
});

export const getMuiThemeOverrides = (
  palette,
  themeOverrides: ThemeOverrides
) => {
  return {
    // Segmented pill tabs (platform TabsList / TabsTrigger)
    MuiTabs: {
      root: {
        display: 'inline-flex',
        maxWidth: '100%',
        minHeight: 0,
        padding: 4,
        borderRadius: radius.pill,
        backgroundColor: color.neutral100,
      },
      vertical: {
        borderRadius: radius.xl,
      },
      flexContainer: {
        gap: 4,
      },
      indicator: {
        display: 'none',
      },
    },
    MuiTab: {
      root: {
        textTransform: 'none' as const,
        fontWeight: 600,
        minHeight: 40,
        minWidth: 0,
        padding: '6px 20px',
        borderRadius: radius.pill,
        transition: 'background-color 150ms ease, box-shadow 150ms ease',
        '@media (min-width: 600px)': {
          minWidth: 0,
        },
        '&:hover:not($selected)': {
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
        },
        '&$selected': {
          backgroundColor: color.surface,
          boxShadow: shadow.sm,
          fontWeight: 700,
        },
      },
      textColorInherit: {
        opacity: 0.8,
      },
    },
    MuiButton: {
      root: {
        borderRadius: radius.pill,
        padding: '8px 20px',
        '&.x-menu-active': {
          '&::after': {
            background: themeOverrides?.appBar?.color ? lighten(themeOverrides?.appBar?.color, .25) : 'none',
          },
        },
      },
      sizeSmall: {
        padding: '4px 14px',
      },
      sizeLarge: {
        padding: '12px 28px',
      },
      text: {
        padding: '8px 16px',
      },
      outlined: {
        padding: '7px 19px',
      },
      outlinedSizeSmall: {
        padding: '3px 13px',
      },
      outlinedSizeLarge: {
        padding: '11px 27px',
      },
    },
    MuiButtonGroup: {
      root: {
        borderRadius: radius.pill,
      },
    },
    MuiIconButton: {
      root: {
        borderRadius: radius.pill,
      },
    },
    MuiFab: {
      root: {
        boxShadow: shadow.md,
      },
    },
    MuiChip: {
      root: {
        fontWeight: 600,
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 30,
      },
    },
    MuiListItem: {
      button: {
        borderRadius: radius.md,
      },
    },
    MuiSelect: {
      select: {
        borderRadius: radius.pill,
        '&:focus': {
          backgroundColor: 'transparent',
          borderRadius: radius.pill,
        },
      },
      icon: {
        fontSize: 20,
        color: color.subtle,
        top: 'calc(50% - 10px)',
      },
      iconOutlined: {
        right: 14,
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: radius.lg,
      },
    },
    MuiCard: {
      root: {
        border: `1px solid ${color.border}`,
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: radius.xxl,
      },
      paperFullScreen: {
        borderRadius: 0,
      },
    },
    MuiPopover: {
      paper: {
        borderRadius: radius.lg,
        border: `1px solid ${color.border}`,
      },
    },
    MuiMenu: {
      list: {
        padding: 4,
      },
    },
    MuiMenuItem: {
      root: {
        borderRadius: radius.md,
        '&:hover': {
          backgroundColor: color.neutral100,
        },
        '&$selected, &$selected:hover': {
          backgroundColor: color.neutral100,
        },
      },
    },
    MuiAutocomplete: {
      // selected values in multi-selects: light blue-grey pills instead of MUI's grey chips
      tag: {
        height: 26,
        borderRadius: radius.pill,
        backgroundColor: color.neutral100,
        color: color.heading,
        fontWeight: 500,
        '& .MuiChip-deleteIcon': {
          width: 16,
          height: 16,
          color: color.subtle,
          opacity: 0.7,
          '&:hover': {
            opacity: 1,
          },
        },
      },
      paper: {
        borderRadius: radius.lg,
        border: `1px solid ${color.border}`,
        boxShadow: shadow.md,
      },
      inputRoot: {
        // Match the 20px text inset of the pill inputs (9px root + 6px input by default)
        '&[class*="MuiOutlinedInput-root"]': {
          paddingLeft: 14,
        },
      },
      listbox: {
        padding: 4,
      },
      popupIndicator: {
        color: color.subtle,
        '& svg': {
          fontSize: 20,
        },
      },
      option: {
        borderRadius: radius.md,
        '&[data-focus="true"]': {
          backgroundColor: color.neutral100,
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: radius.pill,
        '&$multiline': {
          borderRadius: radius.xl,
        },
        // inputs holding chips (multi-selects, watcher pickers) can wrap to several lines:
        // 24px corners instead of the pill, which would otherwise turn into an oval
        '&:has(.MuiChip-root)': {
          borderRadius: 24,
        },
        backgroundColor: color.surface,
        transition: 'box-shadow 150ms ease',
        '&:hover:not($disabled):not($focused):not($error) $notchedOutline': {
          borderColor: color.inputHover,
        },
        '&$focused': {
          boxShadow: `0 0 0 4px ${color.ringShadow}`,
        },
        '&$focused $notchedOutline': {
          borderColor: color.ring,
          borderWidth: 1,
        },
        '&$error$focused': {
          boxShadow: `0 0 0 4px ${status.error.bg}`,
        },
        '&$disabled': {
          backgroundColor: color.neutral50,
          color: color.subtle,
        },
        '&$disabled $notchedOutline': {
          borderColor: color.neutral200,
        },
      },
      input: {
        paddingLeft: 20,
        paddingRight: 20,
      },
      adornedStart: {
        paddingLeft: 20,
      },
      adornedEnd: {
        paddingRight: 20,
      },
      multiline: {
        paddingLeft: 20,
        paddingRight: 20,
      },
      notchedOutline: {
        borderColor: color.input,
        // No notch: the floating label sits on its own white background instead.
        // MUI offsets the outline by -5px to make room for the legend; pin it flush with the input box.
        top: 0,
        '& > legend': {
          display: 'none',
        },
      },
    },
    MuiFilledInput: {
      root: {
        borderRadius: radius.md,
        borderTopLeftRadius: radius.md,
        borderTopRightRadius: radius.md,
      },
    },
    MuiInputLabel: {
      root: {
        '&$focused:not($error)': {
          color: color.ring,
        },
      },
      outlined: {
        transform: 'translate(20px, 20px) scale(1)',
        '&$marginDense': {
          transform: 'translate(20px, 12px) scale(1)',
        },
        '&$shrink': {
          transform: 'translate(16px, -7px) scale(0.75)',
          backgroundColor: color.surface,
          padding: '0 6px',
          borderRadius: radius.pill,
        },
      },
    },
    MuiCheckbox: {
      root: {
        borderRadius: radius.sm,
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: color.foreground,
        borderRadius: radius.md,
        fontSize: 12,
        padding: '6px 12px',
      },
      arrow: {
        color: color.foreground,
      },
    },
    MuiAlert: {
      root: {
        borderRadius: radius.lg,
      },
      standardSuccess: statusAlert('success'),
      standardInfo: statusAlert('info'),
      standardWarning: statusAlert('warning'),
      standardError: statusAlert('error'),
      outlinedSuccess: statusAlert('success'),
      outlinedInfo: statusAlert('info'),
      outlinedWarning: statusAlert('warning'),
      outlinedError: statusAlert('error'),
    },
    MuiAlertTitle: {
      root: {
        fontWeight: 700,
      },
    },
    MuiSnackbarContent: {
      root: {
        borderRadius: radius.lg,
      },
    },
    MuiAccordion: {
      rounded: {
        '&:first-child': {
          borderTopLeftRadius: radius.lg,
          borderTopRightRadius: radius.lg,
        },
        '&:last-child': {
          borderBottomLeftRadius: radius.lg,
          borderBottomRightRadius: radius.lg,
        },
      },
    },
    MuiTableCell: {
      root: {
        borderBottom: `1px solid ${color.border}`,
      },
      head: {
        fontWeight: 700,
        color: color.heading,
      },
    },
    MuiTableRow: {
      hover: {
        '&$hover:hover': {
          backgroundColor: color.neutral50,
        },
      },
    },
    MuiAppBar: {
      root: {
        backgroundColor: themeOverrides?.appBar?.backgroundColor ?? '#FFF',
        '& *': {
          color: themeOverrides?.appBar?.color ?? '#333',
        },
        // inputs in the bar (search boxes, company switcher) are white pills: keep their text,
        // placeholders and icons dark even when the bar's text colour is white (dark bars)
        '& .MuiInputBase-root, & .MuiInputBase-root *': {
          color: color.foreground,
        },
        '& .MuiInputBase-input::placeholder': {
          color: color.subtle,
          opacity: 0.8,
        },
        '& .MuiInputBase-root svg, & .MuiInputAdornment-root, & .MuiInputAdornment-root *': {
          color: color.subtle,
        },
        '& img': {
          backgroundColor:
            themeOverrides?.appBar?.logoBackgroundColor ?? '#FFF',
        },
      },
    },
  };
};
